/**
 * Calendar days in an IANA time zone, with the platform's Intl only (no I/O, no dependency). Used for
 * the per-number daily counters, whose day runs on Pacific time (T-Mobile's cap resets at midnight PT).
 * The full sending-window calculator arrives in M7 (`compliance/window.ts`).
 */

export const PACIFIC = 'America/Los_Angeles';

const formatters = new Map<string, Intl.DateTimeFormat>();
function formatter(timeZone: string): Intl.DateTimeFormat {
  let f = formatters.get(timeZone);
  if (!f) {
    f = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    formatters.set(timeZone, f);
  }
  return f;
}

function parts(timeZone: string, at: Date) {
  const values: Record<string, number> = {};
  for (const p of formatter(timeZone).formatToParts(at)) {
    if (p.type !== 'literal') values[p.type] = Number(p.value);
  }
  return {
    year: values.year ?? 0,
    month: values.month ?? 0,
    day: values.day ?? 0,
    hour: values.hour ?? 0,
    minute: values.minute ?? 0,
    second: values.second ?? 0,
  };
}

/** How far local time in `timeZone` is ahead of UTC at `at`, in milliseconds. */
function offsetMs(timeZone: string, at: Date): number {
  const p = parts(timeZone, at);
  const localAsUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
  return localAsUtc - Math.floor(at.getTime() / 1000) * 1000;
}

/** The calendar date at `at` in `timeZone`, as YYYY-MM-DD. */
export function localDate(timeZone: string, at: Date): string {
  const p = parts(timeZone, at);
  return `${String(p.year).padStart(4, '0')}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`;
}

/**
 * The instant a local wall-clock time (`minuteOfDay` minutes after midnight, 0–1440) on a calendar date
 * occurs in `timeZone`. A time skipped by a spring-forward change resolves to the same moment an hour
 * later (never early); sending windows never start in that hour (they start at 8:00 or later).
 */
export function zonedInstant(timeZone: string, date: string, minuteOfDay: number): Date {
  const [y = 0, m = 1, d = 1] = date.split('-').map(Number);
  const wall = Date.UTC(y, m - 1, d) + minuteOfDay * 60_000;
  // Repeated passes settle the offset even when it differs between the guess and the answer (DST
  // days). Inside a skipped hour the last two passes alternate between the two offsets: take the later
  // moment, so a time that does not exist is never resolved early.
  const a = wall - offsetMs(timeZone, new Date(wall));
  const b = wall - offsetMs(timeZone, new Date(a));
  const c = wall - offsetMs(timeZone, new Date(b));
  return new Date(Math.max(b, c));
}

/** The instant a calendar date (YYYY-MM-DD) begins in `timeZone`. */
export function startOfLocalDay(timeZone: string, date: string): Date {
  return zonedInstant(timeZone, date, 0);
}

/** A calendar date shifted by whole days (YYYY-MM-DD in, YYYY-MM-DD out; no time zone involved). */
export function addDays(date: string, days: number): string {
  const [y = 0, m = 1, d = 1] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

/** Day of the week of a calendar date: 0 = Sunday … 6 = Saturday. */
export function weekdayOf(date: string): number {
  const [y = 0, m = 1, d = 1] = date.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** Throws a RangeError unless `timeZone` is an IANA zone this runtime knows. */
export function assertTimeZone(timeZone: string): void {
  formatter(timeZone);
}

/** The instant the next calendar day begins in `timeZone`, after `at`. */
export function nextLocalMidnight(timeZone: string, at: Date): Date {
  const [y = 0, m = 1, d = 1] = localDate(timeZone, at).split('-').map(Number);
  const next = new Date(Date.UTC(y, m - 1, d + 1));
  return startOfLocalDay(timeZone, next.toISOString().slice(0, 10));
}
